;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import posthog from 'posthog-js';
import {
  isAnalyticsOptedOut,
  isInternalEmail,
  optInAnalyticsInBrowser,
  optOutAnalyticsInBrowser,
} from './opt-out';
import { resolveAnalyticsRoleContext } from './roles';
import type {
  AnalyticsApp,
  AnalyticsEventName,
  AnalyticsEventPayloads,
  AnalyticsProperties,
  AnalyticsRoleContext,
  AnalyticsRoleInput,
} from './types';

interface InitializePostHogOptions {
  app: AnalyticsApp;
  key?: string;
  apiHost?: string;
}

interface IdentifyPostHogUserOptions extends AnalyticsRoleInput {
  app: AnalyticsApp;
  id: number | string;
  email: string | null | undefined;
  firstName?: string | null;
  lastName?: string | null;
}

export function initializePrivacyFirstPostHog({
  app,
  key,
  apiHost = '/ingest',
}: InitializePostHogOptions): void {
  if (!key) return;
  const isOptedOut = typeof document !== 'undefined' && isAnalyticsOptedOut(document.cookie);

  posthog.init(key, {
    api_host: apiHost,
    defaults: '2026-05-30',
    opt_out_capturing_by_default: isOptedOut,
    autocapture: true,
    // Must stay 'history_change': `true` only captures hard loads and skips Next.js
    // client-side navigations (router.push / Link). See README "Pageviews".
    capture_pageview: 'history_change',
    capture_pageleave: true,
    capture_performance: {
      web_vitals: true,
    },
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
    },
    mask_all_text: false,
    mask_all_element_attributes: false,
    before_send: (event) => {
      if (!event) return null;

      const properties: AnalyticsProperties = {
        ...(event.properties as AnalyticsProperties | undefined),
        app,
      };

      return { ...event, properties };
    },
  });
}

export async function identifyPostHogUser({
  app,
  id,
  email,
  firstName,
  lastName,
  roles,
  teams,
  organisationId,
  accessibleApp,
}: IdentifyPostHogUserOptions): Promise<void> {
  const normalizedEmail = email?.trim().toLowerCase();

  if (!normalizedEmail) return;

  const roleContext = resolveAnalyticsRoleContext({
    roles,
    teams,
    organisationId,
    accessibleApp,
  });

  // Registered before the async opt-in so events fired right after identify already
  // carry the segmentation super properties.
  registerRoleSuperProperties(roleContext);

  const isInternalUser = isInternalEmail(normalizedEmail);
  if (isInternalUser) {
    optOutAnalyticsInBrowser();
    posthog.opt_out_capturing();
  } else {
    await optInAnalyticsInBrowser();
  }

  const normalizedFirstName = firstName?.trim() || undefined;
  const normalizedLastName = lastName?.trim() || undefined;
  const name = [normalizedFirstName, normalizedLastName].filter(Boolean).join(' ') || undefined;

  posthog.identify(`user:${id}`, {
    app,
    email: normalizedEmail,
    first_name: normalizedFirstName,
    last_name: normalizedLastName,
    name,
    ...roleContext,
    // null overwrites a stale plan on the person when the user is no longer B2C.
    b2c_plan: roleContext.b2c_plan ?? null,
    organisation_id: organisationId != null ? String(organisationId) : undefined,
  });
}

function registerRoleSuperProperties(roleContext: AnalyticsRoleContext): void {
  posthog.register({
    is_team_lead: roleContext.is_team_lead,
    people_role: roleContext.people_role,
    primary_role: roleContext.primary_role,
    dashboard_group: roleContext.dashboard_group,
    account_type: roleContext.account_type,
  });

  if (roleContext.b2c_plan) {
    posthog.register({ b2c_plan: roleContext.b2c_plan });
  } else {
    posthog.unregister('b2c_plan');
  }
}

export function resetPostHogUser(): void {
  posthog.reset();
}

export function getPostHogDistinctId(): string | undefined {
  return posthog.get_distinct_id() || undefined;
}

export function captureSafePostHogEvent<EventName extends AnalyticsEventName>(
  event: EventName,
  properties: AnalyticsEventPayloads[EventName]
): void {
  posthog.capture(event, properties);
}
